import React from 'react';
import { compact } from 'lodash';
import { shouldShowBumpTag } from '@reverbdotcom/commons/src/components/bump_tag';
import {
  CoreMarketplaceCombinedMarketplaceSearch,
  Ad,
} from '../../schemas/rql';
import SingleBumpTile from './single_bump_tile';
import { PageSettings } from './map_params';
import { IUser } from '@reverbdotcom/commons/src/components/user_context_provider';
import TileCurationView from '../discovery/curation/tile_curation_view';
import ListingCard from '@reverbdotcom/commons/src/components/listing_card';
import { AdCard } from '@reverbdotcom/commons/src/components/ads/ad_card';

interface IProps {
  user: IUser;
  listings: CoreMarketplaceCombinedMarketplaceSearch.Listings[];
  bumpedListings: CoreMarketplaceCombinedMarketplaceSearch.Listings[];
  showOnlySold?: boolean;
  trackingQuery?: string;
  ad?: Ad;
  pageSettings?: PageSettings;
}

const COMPONENT_NAME = 'InterspersedListings';

Listing.defaultProps = {
  showOnlySold: false,
  trackingName: COMPONENT_NAME,
  trackingQuery: '',
};

interface IListingsProps {
  listing: CoreMarketplaceCombinedMarketplaceSearch.Listings;
  idx: number;
  user: IUser;
  showOnlySold?: boolean;
  trackingName?: string;
  trackingQuery?: string;
  pageSettings?: PageSettings;
}

function listingKey(listing: CoreMarketplaceCombinedMarketplaceSearch.Listings): string {
  if (listing.bumpKey?.key) {
    return `${listing.id}-${listing.bumpKey?.key}`;
  }

  return listing.id;
}

export function Listing(props: IListingsProps) {
  return (
    <ListingCard
      key={props.listing.id}
      listing={props.listing}
      position={props.idx}
      soldListing={props.showOnlySold}
      trackingName={props.trackingName}
      redesignedCard
      trackingQuery={props.trackingQuery}
      footerContent={
        props.user.isAdmin &&
        <TileCurationView
          listingId={props.listing.id}
          pageSettings={props.pageSettings}
          bumped={props.listing.bumped}
        />
      }
    />
  );
}

// This function intersperses the bump listings amongst normal listings.
// It is setup so that every 2nd and 3rd tile (of out a set of 7) in the grid are bumped listings.
function intersperseBumps(listings, bumps, starting, frequency) {
  let bumpIndex = 0;
  const interspersedArray = [];
  for (let i = 0; i < listings.length; i += 1) {
    if ((i + starting) % frequency === 0) {
      interspersedArray.push(bumps[bumpIndex], bumps[bumpIndex + 1]);
      bumpIndex += 2;
    }
    interspersedArray.push(listings[i]);
  }

  return compact(interspersedArray);
}

function setTrackingName(listing) {
  return shouldShowBumpTag(listing) ? `Bumped${COMPONENT_NAME}` : COMPONENT_NAME;
}

const TILE_CLASSNAME_BASE = 'sortable-tile';

interface AdListingItemProps {
  ad: Ad;
  idx: number;
}

function AdListingItem(props: AdListingItemProps) {
  return (
    <li
      className={TILE_CLASSNAME_BASE}
      key={props.ad.uuid}
    >
      <div>
        <AdCard
          ad={props.ad}
          key={props.idx}
          componentName="CombinedBumpListingGrid"
        />
      </div>
    </li>
  );
}
interface ListingItemProps {
  listing: CoreMarketplaceCombinedMarketplaceSearch.Listings;
  idx: number;
  user: IUser
  showOnlySold: any;
  trackingQuery?: string;
  pageSettings?: PageSettings;
}

function ListingItem(props: ListingItemProps) {
  const {
    listing,
    idx,
    user,
    showOnlySold,
    trackingQuery,
    pageSettings,
  } = props;

  return (
    <li
      className={TILE_CLASSNAME_BASE}
      key={listingKey(listing)}
    >
      <Listing
        listing={listing}
        idx={idx}
        user={user}
        showOnlySold={showOnlySold}
        trackingName={setTrackingName(listing)}
        trackingQuery={trackingQuery}
        pageSettings={pageSettings}
      />
    </li>
  );
}

function randomAdPosition() {
  // provides a random position in the second row (index positions 5 through 9)
  const AD_POSITION_MIN = 5;
  const AD_POSITION_MAX = 9;
  return Math.floor(Math.random() * (AD_POSITION_MAX - AD_POSITION_MIN + 1) + AD_POSITION_MIN);
}

// used for calculating the idx of listing items if an ad is inserted
function listingItemAdsIndex(
  index: number,
  adIndex = 0,
  isAd: boolean,
): number {
  if (!isAd) return index;

  return index < adIndex ? index : index + 1;
}

interface RenderListingsProps {
  listings: CoreMarketplaceCombinedMarketplaceSearch.Listings[];
  ad: Ad;
  showOnlySold: boolean;
  trackingQuery: string;
  user: IUser;
  pageSettings?: PageSettings
}

function ListingsWithAd(props: RenderListingsProps) {
  const adPosition = randomAdPosition();
  const listingItems = props.listings.map((listing, idx) =>
    <ListingItem
      key={listingKey(listing)}
      listing={listing}
      idx={listingItemAdsIndex(idx, adPosition, !!props.ad)}
      user={props.user}
      showOnlySold={props.showOnlySold}
      trackingQuery={props.trackingQuery}
      pageSettings={props.pageSettings}
    />,
  );

  if (!props.ad) {
    return (
      <>{listingItems}</>
    );
  }

  const adItem = <AdListingItem
    key={props.ad.uuid}
    idx={adPosition}
    ad={props.ad}
  />;

  listingItems.splice(adPosition, 0, adItem);

  return (
    <>{listingItems}</>
  );
}

export default function InterspersedListings(props: IProps) {
  const bumps = props.bumpedListings;
  const { listings } = props;

  if (listings.length < 25) {
    return (
      <ListingsWithAd
        listings={listings}
        ad={props.ad}
        showOnlySold={props.showOnlySold}
        trackingQuery={props.trackingQuery}
        user={props.user}
        pageSettings={props.pageSettings}
      />
    );
  }

  if (bumps.length === 1) {
    return (
      <>
        <div className="sortable-bump-promo">
          <SingleBumpTile
            key={bumps[0].id}
            listing={bumps[0]}
          />
        </div>
        <ListingsWithAd
          listings={listings}
          ad={props.ad}
          showOnlySold={props.showOnlySold}
          trackingQuery={props.trackingQuery}
          user={props.user}
          pageSettings={props.pageSettings}
        />
      </>
    );
  }

  const combinedListings = intersperseBumps(listings, bumps, 4, 5);

  return (
    <ListingsWithAd
      listings={combinedListings}
      ad={props.ad}
      showOnlySold={props.showOnlySold}
      trackingQuery={props.trackingQuery}
      user={props.user}
      pageSettings={props.pageSettings}
    />
  );
}
